import React from "react";
import { CDDCheck } from "../../../../types/cdd-records.types";
import {
  formatDateShort,
  formatDateTime,
} from "../../../../utils/dateFormatters";
import {
  getResultBadgeClass,
  getStatusBadgeClass,
} from "./checkDetailModalConfig";

interface CheckDetailModalProps {
  check: CDDCheck;
  typeConfig: { label: string; icon: React.ReactElement };
  onClose: () => void;
}

export const CheckDetailModal: React.FC<CheckDetailModalProps> = ({
  check,
  typeConfig,
  onClose,
}) => {
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="check-detail-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-2xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div
                className={`p-2.5 rounded-xl ${
                  check.status === "completed"
                    ? "bg-green-100 text-green-600"
                    : check.status === "in-progress"
                      ? "bg-blue-100 text-blue-600"
                      : check.status === "failed"
                        ? "bg-red-100 text-red-600"
                        : "bg-gray-100 text-gray-600"
                }`}
              >
                {typeConfig.icon}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {typeConfig.label}
                </h2>
                <p className="text-sm text-gray-500">
                  Check ID: <span className="font-mono">{check.id}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5 text-gray-400 hover:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-6">
            {/* Status and Result */}
            <div className="flex items-center gap-4">
              <div>
                <span className="text-xs text-gray-500 block mb-1">Status</span>
                <span
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize ${getStatusBadgeClass(check.status)}`}
                >
                  {check.status.replace("-", " ")}
                </span>
              </div>
              {check.result && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">
                    Result
                  </span>
                  <span
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize ${getResultBadgeClass(check.result)}`}
                  >
                    {check.result.replace("-", " ")}
                  </span>
                </div>
              )}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <span className="text-xs text-gray-500 block">Check Date</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDateTime(check.checkDate)}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">
                  Performed By
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {check.performedBy}
                </span>
              </div>
              {check.relatedEntityType && (
                <div>
                  <span className="text-xs text-gray-500 block">
                    Related Entity Type
                  </span>
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {check.relatedEntityType}
                  </span>
                </div>
              )}
              {check.expiryDate && (
                <div>
                  <span className="text-xs text-gray-500 block">
                    Expiry Date
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDateShort(check.expiryDate)}
                  </span>
                </div>
              )}
            </div>

            {/* Check Details */}
            {check.details && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Check Details
                </h4>
                <p className="text-sm text-gray-600 p-4 bg-gray-50 rounded-lg">
                  {check.details}
                </p>
              </div>
            )}

            {/* Data Sources */}
            {check.dataSource && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Data Source
                </h4>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <span className="text-sm text-blue-800">
                    {check.dataSource}
                  </span>
                </div>
              </div>
            )}

            {/* Document References */}
            {check.documentReferences &&
              check.documentReferences.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Related Documents
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {check.documentReferences.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
                      >
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="text-sm text-gray-700">{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Notes */}
            {check.notes && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-yellow-600 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-800">
                      Notes
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      {check.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
